import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { convertToWebP, isConvertibleToWebP, getOptimalConversionSettings } from '@/utils/image-conversion';

// Types for workbook images
export interface WorkbookImage {
  id: string;
  user_id: string;
  workbook_id: string;
  page_id?: string;
  image_url: string;
  storage_path: string;
  filename: string;
  file_size?: number;
  width?: number;
  height?: number;
  alt_text?: string;
  generation_prompt?: string;
  is_generated: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * Comprehensive filename sanitization for Supabase Storage
 * Removes all characters that could cause InvalidKey errors
 */
function sanitizeFilename(filename: string): string {
  return filename
    // Remove file extension first
    .replace(/\.[^/.]+$/, '')
    // Replace any non-alphanumeric characters with underscores
    // This handles: {}, [], (), spaces, hyphens, special chars, etc.
    .replace(/[^a-zA-Z0-9]/g, '_')
    // Remove multiple consecutive underscores
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '')
    // Limit length to prevent overly long filenames
    .slice(0, 50);
}

// Query keys
export const workbookImageKeys = {
  all: ['workbook-images'] as const,
  byWorkbook: (workbookId: string) => [...workbookImageKeys.all, 'workbook', workbookId] as const,
  detail: (id: string) => [...workbookImageKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch all images for a workbook
 */
export function useWorkbookImages(workbookId: string) {
  return useQuery({
    queryKey: workbookImageKeys.byWorkbook(workbookId),
    queryFn: async (): Promise<WorkbookImage[]> => {
      const { data, error } = await supabase
        .from('workbook_images')
        .select('*')
        .eq('workbook_id', workbookId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching workbook images:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!workbookId,
  });
}

/**
 * Hook for uploading images to workbook_images bucket
 */
export function useWorkbookImageUpload() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      workbookId,
      pageId,
      altText = 'Workbook image',
      isGenerated = false,
      generationPrompt,
    }: {
      file: File;
      workbookId?: string; // Make optional to support private uploads (workbook_id = null)
      pageId?: string;
      altText?: string;
      isGenerated?: boolean;
      generationPrompt?: string;
    }): Promise<WorkbookImage> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // 🚀 PHASE 1: AUTOMATIC WEBP CONVERSION
      let processedFile = file;
      let conversionInfo: string | null = null;

      // Convert to WebP if it's a convertible format and not already WebP
      if (file.type !== 'image/webp' && isConvertibleToWebP(file)) {
        try {
          console.log('[useWorkbookImageUpload] Converting to WebP:', file.name);
          
          const conversionSettings = getOptimalConversionSettings(file, 'workbook');
          const result = await convertToWebP(file, conversionSettings);
          
          processedFile = result.file;
          conversionInfo = `Converted from ${result.originalFormat} to WebP (${result.compressionRatio.toFixed(1)}% size reduction)`;
          
          console.log('[useWorkbookImageUpload] WebP conversion successful:', {
            originalSize: result.originalSize,
            newSize: result.newSize,
            savings: `${result.compressionRatio.toFixed(1)}%`
          });
        } catch (error) {
          console.warn('[useWorkbookImageUpload] WebP conversion failed, using original:', error);
          // Continue with original file if conversion fails
        }
      } else if (file.type === 'image/webp') {
        console.log('[useWorkbookImageUpload] File already in WebP format');
      } else {
        console.log('[useWorkbookImageUpload] File type not convertible to WebP, using original');
      }

      // Create filename with comprehensive sanitization
      const timestamp = Date.now();
      // Always use 'webp' extension if we successfully converted, otherwise use original
      const extension = processedFile.type === 'image/webp' ? 'webp' : (processedFile.name.split('.').pop() || 'jpg');
      
      let baseFilename: string;
      if (isGenerated && generationPrompt) {
        // For generated images: sanitize the generation prompt
        const sanitizedPrompt = sanitizeFilename(generationPrompt);
        baseFilename = `generated_${sanitizedPrompt}_${timestamp}`;
      } else {
        // For uploaded images: sanitize the original filename
        const sanitizedName = sanitizeFilename(processedFile.name);
        // Fallback to generic name if sanitization results in empty string
        const safeName = sanitizedName || 'uploaded_image';
        baseFilename = `uploaded_${safeName}_${timestamp}`;
      }
      
      const filename = `${baseFilename}.${extension}`;
      const storagePath = `${userId}/workbook_images/${filename}`;

      console.log('[useWorkbookImageUpload] Sanitized filename:', {
        original: file.name,
        processed: processedFile.name,
        sanitized: filename,
        storagePath,
        conversionInfo
      });

      // Upload to storage (now with WebP file if conversion succeeded)
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('workbook_images')
        .upload(storagePath, processedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('workbook_images')
        .getPublicUrl(storagePath);

      // Get image dimensions from the processed file
      const dimensions = await getImageDimensions(processedFile);

      // Save metadata to database
      const { data: dbData, error: dbError } = await supabase
        .from('workbook_images')
        .insert({
          user_id: userId,
          workbook_id: workbookId,
          page_id: pageId,
          image_url: publicUrl,
          storage_path: storagePath,
          filename,
          file_size: processedFile.size, // Use processed file size
          width: dimensions.width,
          height: dimensions.height,
          alt_text: altText,
          generation_prompt: generationPrompt,
          is_generated: isGenerated,
        })
        .select()
        .single();

      if (dbError) {
        // Cleanup uploaded file if database insert fails
        await supabase.storage.from('workbook_images').remove([storagePath]);
        console.error('Database error:', dbError);
        throw new Error(`Failed to save image metadata: ${dbError.message}`);
      }

      // Show success toast with conversion info
      if (conversionInfo) {
        toast({
          title: "Image uploaded successfully",
          description: conversionInfo,
          variant: "default",
        });
      }

      return dbData;
    },
    onSuccess: (data) => {
      // Invalidate queries to refresh the images list
      queryClient.invalidateQueries({ queryKey: workbookImageKeys.byWorkbook(data.workbook_id) });
      queryClient.invalidateQueries({ queryKey: workbookImageKeys.all });
      
      // Invalidate private image library queries to refresh the private library
      queryClient.invalidateQueries({ queryKey: ['private-image-library', data.workbook_id] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count', data.workbook_id] });
      
      toast({
        title: "Image uploaded successfully",
        description: "Your image has been saved to the workbook.",
      });
    },
    onError: (error: Error) => {
      console.error('Upload mutation error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook for AI image generation and upload
 */
export function useWorkbookImageGeneration() {
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { executeAIOperation, isLoading } = useAIOperationWrapper();
  const { toast } = useToast();

  const generateWorkbookImage = async (
    description: string, 
    styleId?: string, 
    width: string = '1024', 
    height: string = '1024'
  ): Promise<string | null> => {
    if (!description.trim()) {
      setError('Please provide a description for your workbook image');
      toast({
        title: "Description required",
        description: "Please describe the workbook image you want",
        variant: "warning",
      });
      return null;
    }
    
    setError(null);
    
    try {
      console.log('Generating workbook image with params:', {
        additionalText: description,
        imageType: styleId,
        width: parseInt(width),
        height: parseInt(height)
      });
      
      const data = await executeAIOperation(
        async () => {
          // Call the generate-image edge function
          const { data, error } = await supabase.functions.invoke('generate-image', {
            body: {
              imageType: styleId,
              additionalText: description,
              width: parseInt(width),
              height: parseInt(height),
              numberResults: 1,
              format: 'webp',
              quality: 90,
              appName: 'Content Generator',
              functionName: 'Workbook Image Generation',
              // No basePromptId - let user run free as requested
            },
          });

          if (error) {
            console.error('Generation error:', error);
            throw new Error(`Image generation failed: ${error.message}`);
          }

          if (!data || data.error) {
            throw new Error(data?.error || 'Image generation failed');
          }

          return data;
        },
        {
          immediate: true,
          errorPrefix: 'Workbook image generation failed',
          debug: false
        }
      );

      // Extract image data from response - same pattern as useImageGeneration
      if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from image generation');
      }

      const imageResults = data.data.filter((item: any) => item.taskType === 'imageInference');
      
      if (imageResults.length === 0) {
        throw new Error('No images were generated');
      }

      const imageUrl = imageResults[0].imageURL;
      
      if (!imageUrl) {
        throw new Error('No image URL returned from generation');
      }

      setGeneratedImageUrl(imageUrl);
      
      toast({
        title: "Workbook image generated successfully",
        variant: "success",
      });
      
      return imageUrl;
    } catch (err: any) {
      console.error('[useWorkbookImageGeneration] Error generating workbook image:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      
      setError(errorMessage);
      
      toast({
        title: "Failed to generate workbook image",
        description: errorMessage,
        variant: "error",
      });
      
      return null;
    }
  };
  
  return {
    generateWorkbookImage,
    isGenerating: isLoading,
    generatedImageUrl,
    error,
  };
}

/**
 * Hook to soft delete a workbook image (for private library)
 * Sets deleted_at timestamp instead of actually deleting to preserve editor references
 */
export function useSoftDeleteWorkbookImage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ imageId, workbookId }: { imageId: string; workbookId: string }): Promise<void> => {
      console.log('[useSoftDeleteWorkbookImage] Soft delete started:', {
        imageId: imageId,
        workbookId: workbookId,
        timestamp: new Date().toISOString()
      });
      
      // Soft delete by setting deleted_at timestamp
      const { error } = await supabase
        .from('workbook_images')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', imageId);

      console.log('[useSoftDeleteWorkbookImage] Soft delete result:', {
        success: !error,
        error: error,
        imageId: imageId,
        timestamp: new Date().toISOString()
      });

      if (error) {
        console.error('[useSoftDeleteWorkbookImage] Soft delete failed:', {
          imageId: imageId,
          workbookId: workbookId,
          error: error,
          errorCode: error.code,
          errorMessage: error.message,
          timestamp: new Date().toISOString()
        });
        throw new Error(`Failed to delete image: ${error.message}`);
      }
      
      console.log('[useSoftDeleteWorkbookImage] Soft delete completed successfully for:', imageId);
    },
    onSuccess: (_, { imageId, workbookId }) => {
      // Invalidate all image queries
      queryClient.invalidateQueries({ queryKey: workbookImageKeys.all });
      queryClient.invalidateQueries({ queryKey: workbookImageKeys.byWorkbook(workbookId) });
      
      // Invalidate private image library queries
      queryClient.invalidateQueries({ queryKey: ['private-image-library', workbookId] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count', workbookId] });
      
      toast({
        title: "Image removed",
        description: "The image has been removed from your private library.",
      });
    },
    onError: (error: Error) => {
      console.error('Soft delete mutation error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to remove image. Please try again.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to permanently delete a workbook image (hard delete)
 * This removes the image completely including from storage
 */
export function useDeleteWorkbookImage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (imageId: string): Promise<void> => {
      // Get image data first to get storage path
      const { data: imageData, error: fetchError } = await supabase
        .from('workbook_images')
        .select('*')
        .eq('id', imageId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch image data: ${fetchError.message}`);
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('workbook_images')
        .remove([imageData.storage_path]);

      if (storageError) {
        console.error('Storage deletion error:', storageError);
        // Continue with database deletion even if storage fails
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('workbook_images')
        .delete()
        .eq('id', imageId);

      if (dbError) {
        throw new Error(`Failed to delete image record: ${dbError.message}`);
      }
      
      // Note: Universal storage cleanup is now automatic via CASCADE foreign key
    },
    onSuccess: (_, imageId) => {
      // Invalidate all image queries
      queryClient.invalidateQueries({ queryKey: workbookImageKeys.all });
      
      // Invalidate private image library queries (we don't have workbookId here, so invalidate all)
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] });
      
      toast({
        title: "Image deleted",
        description: "The image has been permanently removed.",
      });
    },
    onError: (error: Error) => {
      console.error('Delete mutation error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image. Please try again.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Utility function to get image dimensions
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for dimension calculation'));
    };
    
    img.src = url;
  });
} 